import React, { useState, useEffect, useMemo } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaPhone, FaClock } from 'react-icons/fa';
import { useBooking } from '../../contexts/BookingContext';
import logoImage from '../../assets/images/logo.png';
import { useSiteInfo } from '../../contexts/SiteInfoContext';
import { getAccreditationInfo } from '../../utils/businessInfo';
import './Navbar.css';
import { normalizePhoneNumbers } from '../../utils/businessInfo';

const DEFAULT_BUSINESS_HOURS = {
  monday: { open: '09:30', close: '17:30', closed: false },
  tuesday: { open: '09:30', close: '17:30', closed: false },
  wednesday: { open: '09:30', close: '17:30', closed: false },
  thursday: { open: '09:30', close: '17:30', closed: false },
  friday: { open: null, close: null, closed: true },
  saturday: { open: '09:30', close: '17:30', closed: false },
  sunday: { open: null, close: null, closed: true },
};

const WEEKDAY_SHORT_TO_KEY = {
  Sun: 'sunday',
  Mon: 'monday',
  Tue: 'tuesday',
  Wed: 'wednesday',
  Thu: 'thursday',
  Fri: 'friday',
  Sat: 'saturday',
};

const DAY_KEYS_SEQUENCE = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

const DAY_FULL_LABELS = {
  sunday: 'Sunday',
  monday: 'Monday',
  tuesday: 'Tuesday',
  wednesday: 'Wednesday',
  thursday: 'Thursday',
  friday: 'Friday',
  saturday: 'Saturday',
};

const HOUR_FORMATTER = new Intl.DateTimeFormat('en-AU', {
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
  timeZone: 'Australia/Melbourne',
});

const timeStringToDecimal = (value) => {
  if (!value) {
    return null;
  }
  const [hours, minutes] = value.split(':').map((segment) => Number(segment));
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return null;
  }
  return hours + minutes / 60;
};

const normalizePhoneHref = (value) => {
  if (!value) {
    return '';
  }
  return value.replace(/[^\d+]/g, '');
};
const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { openBookingModal } = useBooking();
  const { siteInfo } = useSiteInfo();
  const logoUrl = logoImage;
  const company = siteInfo?.company ?? {};
  const businessInfo = siteInfo?.business_info ?? {};
  const [businessStatus, setBusinessStatus] = useState({
    isOpen: false,
    label: 'Closed',
    message: 'Opens Monday 9:30 AM AEST',
  });

  const contactPhones = useMemo(
    () => normalizePhoneNumbers(businessInfo, company),
    [businessInfo, company]
  );

  const fallbackPhone = company.phone || '0383682249';
  const primaryContactPhone = contactPhones[0]?.number || fallbackPhone;
  const topBarCallHref = `tel:${normalizePhoneHref(primaryContactPhone)}`;
  const showCallNow = businessStatus.isOpen;

  const businessHours = useMemo(
    () => siteInfo?.hours ?? DEFAULT_BUSINESS_HOURS,
    [siteInfo?.hours]
  );

  const accreditation = getAccreditationInfo(businessInfo, siteInfo?.site_settings ?? {});
  const { logo: vaccLogoUrl, link: vaccLink } = accreditation;
  const hideAccreditation = (event) => {
    event.currentTarget.style.display = 'none';
  };

  // Check for special closure first, then use normal hours
  const specialClosure = businessInfo.special_closure;
  const isClosedToday = specialClosure && specialClosure.type === 'full_day';

  const topBarHoursText = isClosedToday
    ? specialClosure.message || 'Closed today'
    : (siteInfo?.site_settings?.['topbar.hours'] ||
       businessInfo.hours_summary ||
       'Mon-Thu & Sat: 9:30 AM - 5:30 PM');

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const melbourneFormatter = new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Melbourne',
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    });

    const timeDisplayFormatter = new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Melbourne',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

    const timeZoneFormatter = new Intl.DateTimeFormat('en-AU', {
      timeZone: 'Australia/Melbourne',
      timeZoneName: 'short',
    });

    const getZoneAbbr = () => {
      const parts = timeZoneFormatter.formatToParts(new Date());
      return parts.find((part) => part.type === 'timeZoneName')?.value || 'AEST';
    };

    const formatTime = (timeString) => {
      if (!timeString) {
        return '';
      }
      const [hours, minutes] = timeString.split(':').map((segment) => Number(segment));
      if (Number.isNaN(hours) || Number.isNaN(minutes)) {
        return '';
      }
      const referenceDate = new Date();
      referenceDate.setHours(hours, minutes, 0, 0);
      return timeDisplayFormatter.format(referenceDate);
    };

    const computeStatus = () => {
      // Check for special closure first
      const specialClosure = businessInfo.special_closure;
      if (specialClosure) {
        setBusinessStatus({
          isOpen: false,
          label: 'Closed',
          message: specialClosure.message || 'Closed for special event',
        });
        return;
      }

      const parts = melbourneFormatter.formatToParts(new Date());
      const weekdayValue = parts.find((part) => part.type === 'weekday')?.value || 'Mon';
      const hourValue = Number(parts.find((part) => part.type === 'hour')?.value || 0);
      const minuteValue = Number(parts.find((part) => part.type === 'minute')?.value || 0);
      const currentTimeDecimal = hourValue + minuteValue / 60;
      const zoneAbbr = getZoneAbbr();
      const todayKey = WEEKDAY_SHORT_TO_KEY[weekdayValue.slice(0, 3)] || 'monday';
      const todaySchedule = businessHours[todayKey] ?? {};
      const todayOpenDecimal = timeStringToDecimal(todaySchedule.open);
      const todayCloseDecimal = timeStringToDecimal(todaySchedule.close);
      const isOperatingDay =
        todaySchedule && !todaySchedule.closed && todayOpenDecimal !== null && todayCloseDecimal !== null;

      let isCurrentlyOpen = false;
      let message = '';

      if (isOperatingDay && currentTimeDecimal >= todayOpenDecimal && currentTimeDecimal < todayCloseDecimal) {
        isCurrentlyOpen = true;
        message = `Closes today at ${formatTime(todaySchedule.close)} ${zoneAbbr}`;
      } else if (isOperatingDay && currentTimeDecimal < todayOpenDecimal) {
        message = `Opens today at ${formatTime(todaySchedule.open)} ${zoneAbbr}`;
      } else {
        const currentIndex = DAY_KEYS_SEQUENCE.indexOf(todayKey);
        let offset = 1;
        let nextDayKey = todayKey;
        while (offset <= 7) {
          const candidateKey = DAY_KEYS_SEQUENCE[(currentIndex + offset) % 7];
          const candidate = businessHours[candidateKey] ?? {};
          if (candidate && !candidate.closed && candidate.open && candidate.close) {
            nextDayKey = candidateKey;
            break;
          }
          offset += 1;
        }

        const nextDaySchedule = businessHours[nextDayKey] ?? {};
        const nextDayLabel = DAY_FULL_LABELS[nextDayKey] || 'Monday';
        const nextDayOpenLabel = formatTime(nextDaySchedule.open) || formatTime('09:30');
        const dayText = offset === 1 ? 'tomorrow' : `on ${nextDayLabel}`;
        message = `Opens ${dayText} at ${nextDayOpenLabel} ${zoneAbbr}`;
      }

      setBusinessStatus({
        isOpen: isCurrentlyOpen,
        label: isCurrentlyOpen ? 'Open now' : 'Closed',
        message,
      });
    };

    computeStatus();
    const interval = setInterval(computeStatus, 60000);
    return () => clearInterval(interval);
  }, [businessHours, businessInfo]);

  const toggleMenu = () => setIsOpen(!isOpen);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About Us' },
    { path: '/services', label: 'Services' },
    { path: '/contact', label: 'Contact' },
  ];

  return (
    <>
      {/* Top Bar */}
      <div className="top-bar">
        <div className="container">
          <div className="top-bar-content">
          <div className="top-bar-left">
            <div className="top-bar-contact">
              <FaPhone />
              <div className="top-bar-contact-numbers">
                {contactPhones.length > 0 ? (
                  contactPhones.map((phone, index) => (
                    <React.Fragment key={`${phone.number}-${index}`}>
                      <a href={`tel:${normalizePhoneHref(phone.number)}`} className="top-bar-link">
                        {phone.number}
                      </a>
                      {index < contactPhones.length - 1 && (
                        <span className="phone-separator">•</span>
                      )}
                    </React.Fragment>
                  ))
                ) : (
                  <a href="tel:0383682249" className="top-bar-link">
                    (03) 8368 2249
                  </a>
                )}
              </div>
            </div>
            <div className="business-status">
              <span className={`status-pill ${businessStatus.isOpen ? 'open' : 'closed'}`}>
                <span className="status-dot" />
                {businessStatus.label}
              </span>
              <span className="status-note">
                {businessStatus.message}
              </span>
            </div>
          </div>
          <div className={`top-bar-hours-summary ${isClosedToday ? 'special-closure' : ''}`}>
            <FaClock />
            <span style={isClosedToday ? { color: '#ff6b6b', fontWeight: 'bold' } : {}}>
              {isClosedToday && '🔴 '}
              {topBarHoursText}
            </span>
          </div>
          <div className="top-bar-right">
            {showCallNow ? (
              <a href={topBarCallHref} className="top-bar-action" aria-label="Call Automotive Care">
                Call now
              </a>
            ) : (
              <button
                type="button"
                className="top-bar-action btn btn-sm btn-primary"
                onClick={openBookingModal}
              >
                Book Now
              </button>
            )}
          </div>
        </div>
      </div>
    </div>

      {/* Main Navbar */}
      <motion.nav
        className={`navbar ${scrolled ? 'scrolled' : ''}`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="container">
          <div className="navbar-content">
            {/* Logo */}
            <Link to="/" className="navbar-brand" aria-label="Automotive Care home">
              <motion.div
                whileHover={{ scale: 1.05 }}
                transition={{ type: 'spring', stiffness: 300 }}
                className="logo-lockup"
              >
                <img
                  src={logoUrl}
                  alt="Automotive Care logo"
                  className="logo-image"
                  loading="lazy"
                  width={220}
                  height={90}
                />
                <span className="sr-only">Automotive Care</span>
                {/* Legacy wordmark retained for quick rollback */}
                {/*
                <h1 className="logo-text">Automotive Care</h1>
                <p className="logo-tagline">Expert Service Since 2017</p>
                */}
              </motion.div>
            </Link>

            {/* Desktop Navigation */}
            <div className="navbar-links desktop-only">
              {navLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) =>
                    isActive ? 'nav-link active' : 'nav-link'
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </div>
            {vaccLogoUrl && (
              <div className="navbar-accreditation desktop-only">
                {vaccLink ? (
                  <a
                    href={vaccLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="VACC accreditation"
                  >
                    <img
                      src={vaccLogoUrl}
                      alt="VACC Accredited Member"
                      loading="lazy"
                      onError={hideAccreditation}
                    />
                  </a>
                ) : (
                  <img
                    src={vaccLogoUrl}
                    alt="VACC Accredited Member"
                    loading="lazy"
                    onError={hideAccreditation}
                  />
                )}
              </div>
            )}

            {/* Mobile Menu Toggle */}
            <button
              className="menu-toggle mobile-only"
              onClick={toggleMenu}
              aria-label="Toggle menu"
            >
              {isOpen ? <FaTimes /> : <FaBars />}
            </button>
          </div>
        </div>
      </motion.nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="mobile-menu"
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.3 }}
          >
            <div className="mobile-menu-content">
              {navLinks.map((link, index) => (
                <motion.div
                  key={link.path}
                  initial={{ opacity: 0, x: 50 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                >
                  <NavLink
                    to={link.path}
                    className={({ isActive }) =>
                      isActive ? 'mobile-link active' : 'mobile-link'
                    }
                    onClick={() => setIsOpen(false)}
                  >
                    {link.label}
                  </NavLink>
                </motion.div>
              ))}
              {/* Mobile Status & CTA */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mobile-menu-status"
              >
                <div className={`mobile-status-badge ${businessStatus.isOpen ? 'open' : 'closed'}`}>
                  <span className="status-dot" />
                  <span className="status-label">{businessStatus.label}</span>
                  <span className="status-time">{businessStatus.message}</span>
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="mobile-menu-cta"
              >
                {businessStatus.isOpen ? (
                  <a
                    href={topBarCallHref}
                    className="btn btn-primary btn-block"
                    onClick={() => setIsOpen(false)}
                  >
                    Call Now
                  </a>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary btn-block"
                    onClick={() => {
                      setIsOpen(false);
                      openBookingModal();
                    }}
                  >
                    Book Now
                  </button>
                )}
              </motion.div>
              {vaccLogoUrl && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  className="mobile-menu-accreditation"
                >
                  {vaccLink ? (
                    <a
                      href={vaccLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="VACC accreditation"
                    >
                      <img
                        src={vaccLogoUrl}
                        alt="VACC Accredited Member"
                        loading="lazy"
                        onError={hideAccreditation}
                      />
                    </a>
                  ) : (
                    <img
                      src={vaccLogoUrl}
                      alt="VACC Accredited Member"
                      loading="lazy"
                      onError={hideAccreditation}
                    />
                  )}
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
